import { useCallback, useEffect, useRef } from "react";
import { AppState } from "react-native";
import type { DeviceTokenApi } from "../device-token-api";
import { PermissionDeniedError, RegistrationCancelledError, useDeviceToken } from "../use-device-token";

/**
 * Where this device's registered token is remembered between launches. The
 * app's auto-registration (`usePushNotifications`' `shouldAutoRegisterToken`)
 * reads the same slot, so the switch and TTL refresh stay in agreement.
 */
export interface StoredDeviceToken {
  readonly get: () => string | null;
  readonly set: (token: string) => void;
  readonly remove: () => void;
}

export interface UsePushNotificationSwitchOptions {
  readonly deviceTokenApi: DeviceTokenApi;
  readonly storedToken: StoredDeviceToken;
  readonly showSuccessToast: (message: string) => void;
  readonly showErrorToast: (message: string) => void;
  readonly openSystemSettings: () => void;
  /** Gates the registration status query, e.g. on the push feature flag. */
  readonly enabled?: boolean;
  /**
   * `"all"` (the default) confirms every change and explains a denied
   * permission. `"unexpectedErrorsOnly"` stays quiet unless something actually
   * went wrong — for onboarding, where the switch and the system-settings row
   * already show the outcome and a toast would only interrupt the flow.
   */
  readonly feedback?: "all" | "unexpectedErrorsOnly";
}

export interface PushNotificationSwitchState {
  /** Whether this device's token is registered — the switch's position. */
  readonly isEnabled: boolean;
  readonly isLoading: boolean;
  readonly isBusy: boolean;
  /** Notification permission was denied at the OS level; only system settings can undo that. */
  readonly isDeniedAtOsLevel: boolean;
  readonly onToggle: () => void;
  readonly onOpenSystemSettings: () => void;
}

const GENERIC_ERROR = "Something went wrong. Please try again.";

/**
 * Registers or unregisters this device's push token. Deliberately writes no
 * notification preferences: which sites and cameras push is sent for is the
 * Push column of the preferences form, and this only decides whether this
 * device can receive what that column opts in to.
 */
export function usePushNotificationSwitch({
  deviceTokenApi,
  storedToken,
  showSuccessToast,
  showErrorToast,
  openSystemSettings,
  enabled = true,
  feedback = "all",
}: UsePushNotificationSwitchOptions): PushNotificationSwitchState {
  // Read by the register guard mid-flight: toggling off while a register is
  // still in progress rolls that registration back instead of leaving a
  // token behind the user just switched off.
  const shouldRemainRegisteredRef = useRef(false);
  const shouldRemainRegistered = useCallback((_token: string) => shouldRemainRegisteredRef.current, []);

  const { isRegistered, register, unregister } = useDeviceToken({
    deviceTokenApi,
    enabled,
    shouldRemainRegistered,
  });
  const refetchIsRegistered = isRegistered.refetch;

  const data = isRegistered.data ?? { permission: "undetermined" as const, registered: false };
  const isEnabled = data.registered;
  const isBusy = register.isPending || unregister.isPending;

  useEffect(() => {
    if (!register.isPending && !unregister.isPending) {
      shouldRemainRegisteredRef.current = isEnabled;
    }
  }, [isEnabled, register.isPending, unregister.isPending]);

  // Permission may have been changed in system settings while the app was
  // backgrounded.
  useEffect(() => {
    if (!enabled) return;
    const subscription = AppState.addEventListener("change", (status) => {
      if (status === "active") void refetchIsRegistered();
    });
    return () => subscription.remove();
  }, [enabled, refetchIsRegistered]);

  const onToggle = () => {
    if (isEnabled) {
      shouldRemainRegisteredRef.current = false;
      const knownToken = storedToken.get() ?? data.token;
      storedToken.remove();
      unregister.mutate(
        { ...(knownToken !== undefined && { token: knownToken }), lookupToken: false },
        {
          onSuccess: () => {
            if (feedback === "all") showSuccessToast("Notifications disabled");
          },
          onError: () => {
            if (knownToken) storedToken.set(knownToken);
            showErrorToast(GENERIC_ERROR);
          },
        },
      );
      return;
    }

    shouldRemainRegisteredRef.current = true;
    register.mutate(undefined, {
      onSuccess: ({ token }) => {
        storedToken.set(token);
        if (feedback === "all") showSuccessToast("Notifications enabled");
      },
      onError: (error) => {
        if (error instanceof PermissionDeniedError) {
          if (feedback === "all") showErrorToast("Permission not granted. Enable in Settings.");
        } else if (error instanceof RegistrationCancelledError) {
          shouldRemainRegisteredRef.current = false;
        } else {
          showErrorToast(GENERIC_ERROR);
        }
      },
    });
  };

  return {
    isEnabled,
    isLoading: isRegistered.isLoading,
    isBusy,
    isDeniedAtOsLevel: data.permission === "denied",
    onToggle,
    onOpenSystemSettings: openSystemSettings,
  };
}
